import { createSlice, createSelector} from "@reduxjs/toolkit";
import { loadMilestoneTaskEstimateChangedAlertsAsync } from "./milestoneTaskEstimateChangedAlertApi";
import { RootState } from "../../app/store";
import { initialAlertsState } from "./alertsState";
import { loadUserAlertReadAsync } from "./alertsApi";
import { AlertTypeEnum } from "./AlertTypeEnum";
import { loadClockifyTimeChangedAlertsByMilestoneIdAsync } from "./clockifyTimeChangedAlert/clockifyTimeEntryTimeChangedApi";
import { loadPercentageCompleteChangedAlertsByMilestoneIdAsync } from "./percentageCompleteChangedAlert/percentageCompleteChangedAlertApi";

export const alertsSlice = createSlice({
  name: "alerts",
  initialState: initialAlertsState,
  reducers: {
    addMilestoneTaskEstimateProcessedAlertIds: (state, action) => {
      state.milestoneTaskEstimateProcessedAlertIds = [
        ...state.milestoneTaskEstimateProcessedAlertIds,
        ...action.payload,
      ];
    },
    addClockifyTimeEntryTimeProcessedAlertIds: (state, action) => {
      state.clockifyTimeEntryTimeChangedReadAlerts = [
        ...state.clockifyTimeEntryTimeChangedReadAlerts,
        ...action.payload,
      ];
    },
    addPercentageCompleteProcessedAlertIds: (state, action) => {
      state.percentageCompleteProcessedAlertIds = [
        ...state.percentageCompleteProcessedAlertIds,
        ...action.payload,
      ];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadMilestoneTaskEstimateChangedAlertsAsync.pending, (state) => {
        state.milestoneTaskEstimateAlertState.initializationStatus =
          "initializing";
      })
      .addCase(
        loadMilestoneTaskEstimateChangedAlertsAsync.fulfilled,
        (state, action) => {
          state.milestoneTaskEstimateAlertState.milestoneTaskEstimateChangedAlerts =
            action.payload;
          state.milestoneTaskEstimateAlertState.initializationStatus =
            "initialized";
        }
      )
      .addCase(
        loadMilestoneTaskEstimateChangedAlertsAsync.rejected,
        (state) => {
          state.milestoneTaskEstimateAlertState.initializationStatus =
            "initialized";
        }
      )
      .addCase(loadUserAlertReadAsync.fulfilled, (state, action) => {
        if (!action.payload) {
          return;
        }

        const readAlertIds = action.payload.readAlertIds;
        if (action.payload.alertType === AlertTypeEnum.MilestoneTaskEstimate) {
          const filteredAlerts =
            state.milestoneTaskEstimateAlertState.milestoneTaskEstimateChangedAlerts.filter(
              (alert) => readAlertIds.includes(alert.id)
            );
          state.milestoneTaskEstimateAlertState.milestoneTaskEstimateReadAlerts =
            filteredAlerts;
          state.milestoneTaskEstimateAlertState.initializationStatus =
            "initialized";
        } else if (action.payload.alertType === AlertTypeEnum.TimeEntryTime) {
          const filteredAlerts =
            state.clockifyTimeEntryTimeAlertState.clockifyTimeChangedAlerts.filter(
              (alert) => readAlertIds.includes(alert.id)
            );
          state.clockifyTimeEntryTimeAlertState.clockifyTimeChangedReadAlerts =
            filteredAlerts;
          state.clockifyTimeEntryTimeAlertState.alertReadEventInitializationStatus =
            "initialized";
        } else if (
          action.payload.alertType === AlertTypeEnum.PercentageComplete
        ) {
          const filteredAlerts =
            state.percentageCompleteChangedAlertState.percentageCompleteChangedAlerts.filter(
              (alert) => readAlertIds.includes(alert.id)
            );
          state.percentageCompleteChangedAlertState.percentageCompleteChangedReadAlerts =
            filteredAlerts;
          state.percentageCompleteChangedAlertState.alertReadEventInitializationStatus =
            "initialized";
        }
      })
      .addCase(loadUserAlertReadAsync.rejected, (state) => {
        state.milestoneTaskEstimateAlertState.alertReadEventInitializationStatus =
          "initialized";
      })
      .addCase(
        loadClockifyTimeChangedAlertsByMilestoneIdAsync.pending,
        (state) => {
          state.clockifyTimeEntryTimeAlertState.clockifyTimeChangedAlerts = [];
          state.clockifyTimeEntryTimeAlertState.initializationStatus =
            "initializing";
        }
      )
      .addCase(
        loadClockifyTimeChangedAlertsByMilestoneIdAsync.fulfilled,
        (state, action) => {
          state.clockifyTimeEntryTimeAlertState.clockifyTimeChangedAlerts =
            action.payload;
          state.clockifyTimeEntryTimeAlertState.initializationStatus =
            "initialized";
        }
      )
      .addCase(
        loadClockifyTimeChangedAlertsByMilestoneIdAsync.rejected,
        (state) => {
          state.clockifyTimeEntryTimeAlertState.initializationStatus =
            "initialized";
        }
      )
      .addCase(
        loadPercentageCompleteChangedAlertsByMilestoneIdAsync.pending,
        (state) => {
          state.percentageCompleteChangedAlertState.initializationStatus =
            "initializing";
        }
      )
      .addCase(
        loadPercentageCompleteChangedAlertsByMilestoneIdAsync.fulfilled,
        (state, action) => {
          state.percentageCompleteChangedAlertState.percentageCompleteChangedAlerts =
            action.payload;
          state.percentageCompleteChangedAlertState.initializationStatus =
            "initialized";
        }
      )
      .addCase(
        loadPercentageCompleteChangedAlertsByMilestoneIdAsync.rejected,
        (state) => {
          state.percentageCompleteChangedAlertState.initializationStatus =
            "initialized";
        }
      );
  },
});

export const {
  addMilestoneTaskEstimateProcessedAlertIds,
  addClockifyTimeEntryTimeProcessedAlertIds,
  addPercentageCompleteProcessedAlertIds,
} = alertsSlice.actions;

const selectMilestoneTaskEstimateChangedAlerts = (state: RootState) =>
  state.alerts.milestoneTaskEstimateAlertState
    .milestoneTaskEstimateChangedAlerts;

const selectMilestoneTaskEstimateReadAlerts = (state: RootState) =>
  state.alerts.milestoneTaskEstimateAlertState.milestoneTaskEstimateReadAlerts;

export const selectClockifyTimeChangedAlerts = (state: RootState) =>
  state.alerts.clockifyTimeEntryTimeAlertState.clockifyTimeChangedAlerts;

const selectClockifyTimeChangedReadAlerts = (state: RootState) =>
  state.alerts.clockifyTimeEntryTimeAlertState.clockifyTimeChangedReadAlerts;

export const selectPercentageCompleteChangedAlerts = (state: RootState) =>
  state.alerts.percentageCompleteChangedAlertState
    .percentageCompleteChangedAlerts;

export const selectPercentageCompleteChangedReadAlerts = (state: RootState) =>
  state.alerts.percentageCompleteChangedAlertState
    .percentageCompleteChangedReadAlerts;

export const selectClockifyTimeChangedAlertsCount = createSelector(
  selectClockifyTimeChangedAlerts,
  (alerts) => (alerts || []).length
);

export const selectMilestoneEstimateProcessedAlertIds = (state: RootState) =>
  state.alerts.milestoneTaskEstimateAlertState.processedAlertIds;

export const selectNonReadClockifyTimeChangedAlerts = createSelector(
  [selectClockifyTimeChangedAlerts, selectClockifyTimeChangedReadAlerts],
  (allAlerts, readAlerts) =>
    allAlerts.filter(
      (alert) =>
        !(readAlerts || []).some((readAlert) => readAlert.id === alert.id)
    )
);

export const selectNonReadClockifyTimeChangedAlertsCount = createSelector(
  selectNonReadClockifyTimeChangedAlerts,
  (nonReadAlerts) => nonReadAlerts.length
);

export const selectClockifyTimeEntryTimeChangedAlerts = (state: RootState) =>
  state.alerts.clockifyTimeEntryTimeAlertState.clockifyTimeChangedAlerts;

export const selectClockifyTimeEntryTimeChangedReadAlerts = (
  state: RootState
) => state.alerts.clockifyTimeEntryTimeAlertState.clockifyTimeChangedReadAlerts;

export const selectClockifyTimeEntryTimeChangedProcessedAlertIds = (
  state: RootState
) => state.alerts.clockifyTimeEntryTimeAlertState.processedAlertIds;

export const selectPercentageCompleteChangedAlertProcessedAlertIds = (
  state: RootState
) => state.alerts.percentageCompleteChangedAlertState.processedAlertIds;

export const selectPercentageCompleteChangedReadAlertsIds = createSelector(
  [selectPercentageCompleteChangedReadAlerts],
  (readAlerts) => readAlerts.map((alert) => alert.id)
);

export const selectMilestoneTaskEstimateAlertInitializationStatus = (
  state: RootState
) => state.alerts.milestoneTaskEstimateAlertState.initializationStatus;

export const selectPercentageCompleteChangedAlertInitializationStatus = (
  state: RootState
) => state.alerts.percentageCompleteChangedAlertState.initializationStatus;

export const selectClockifyTimeEntryTimeAlertInitializationStatus = (
  state: RootState
) => state.alerts.clockifyTimeEntryTimeAlertState.initializationStatus;

export const selectLastMilestoneTaskEstimateAlertReadEvent = (
  state: RootState
) =>
  state.alerts.milestoneTaskEstimateAlertState.milestoneTaskEstimateReadAlerts;

export const selectNonReadSelectedMilestoneTaskEstimateAlerts = createSelector(
  [
    selectMilestoneTaskEstimateChangedAlerts,
    selectMilestoneTaskEstimateReadAlerts,
  ],
  (allAlerts, readAlerts) =>
    allAlerts.filter(
      (alert) =>
        !(readAlerts || []).some((readAlert) => readAlert.id === alert.id)
    )
);

export const selectNonReadMilestoneTaskEstimateAlertsCount = createSelector(
  selectNonReadSelectedMilestoneTaskEstimateAlerts,
  (nonReadAlerts) => nonReadAlerts.length
);

export const selectNonReadClockifyTimeEntryTimeChangedAlertsCount =
  createSelector(
    selectNonReadClockifyTimeChangedAlerts,
    (nonReadAlerts) => nonReadAlerts.length
  );

export const selectNonReadPercentageCompleteChangedAlerts = createSelector(
  [
    selectPercentageCompleteChangedAlerts,
    selectPercentageCompleteChangedReadAlerts,
  ],
  (allAlerts, readAlerts) =>
    allAlerts.filter(
      (alert) =>
        !(readAlerts || []).some((readAlert) => readAlert.id === alert.id)
    )
);

export const selectTotalUnreadAlertsCount = createSelector(
  selectNonReadClockifyTimeChangedAlerts,
  selectNonReadSelectedMilestoneTaskEstimateAlerts,
  selectNonReadPercentageCompleteChangedAlerts,
  (clockifyAlerts, milestoneAlerts, percentageAlerts) =>
    clockifyAlerts.length + milestoneAlerts.length + percentageAlerts.length
);

export default alertsSlice.reducer;
